import Vue from 'vue';
import Component from 'vue-class-component';
import * as i18next from 'i18next';
import router from 'src/router';
import { required, minLength, email } from 'vuelidate/lib/validators';
import { validateEmailOrPhone } from 'core/validators';
import * as auth from 'core/auth';
import moment from 'moment';
import * as api from 'core/api';
import * as utils from 'core/utils';
import { setInterval, clearInterval } from 'timers';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

type PanelSigninComponentState = 'welcome' | 'password' | 'forgottenPassword' | 'registration' | 'checkEmail' | 'smsWelcome' | 'oneTimePassword';

export interface PanelSigninComponentModel {
    checkEmailType: string;
    currentUserLoaded: boolean;
    error: string;
    email: string;
    emailError: string;
    password: string;
    passwordError: string;
    previousState: PanelSigninComponentState | '';
    registrationEmail: string;
    registrationEmailError: string;
    registrationPassword: string;
    state: PanelSigninComponentState;
    submitted: boolean;
    transition: string;
    welcomeEmailOrPhone: string;
    welcomeEmailOrPhoneError: string;
    oneTimePassword: string;
    oneTimePasswordError: string;
}

@WithRender
@Component({
    validations: {
        model: {
            password: { required, minLength: minLength(6) },
            email: { required, email },
            registrationEmail: { required, email },
            registrationPassword: { required, minLength: minLength(6) },
            welcomeEmailOrPhone: { required, minLength: minLength(5), validateEmailOrPhone },
            oneTimePassword: { required, minLength: minLength(6)},
        }
    }
})
export class PanelSigninComponent extends Vue {
    $auth: auth.AuthService; // Injected in G2S.init

    model: PanelSigninComponentModel = {
        checkEmailType: '',
        currentUserLoaded: false,
        error: '',
        email: '',
        emailError: '',
        password: '',
        passwordError: '',
        previousState: '',
        registrationEmail: '',
        registrationEmailError: '',
        registrationPassword: '',
        state: 'welcome',
        submitted: false,
        transition: 'slide-right',
        welcomeEmailOrPhone: '',
        welcomeEmailOrPhoneError: '',
        oneTimePassword: '',
        oneTimePasswordError: '',
    };

    secondsLeftUntilNextSms: number = 0;
    isNextSmsAvaliable: boolean = true;

    allowCms: boolean = false;

    tooltipActivator: string | null = null;
    tooltipText: string | null = null;
    tooltipVisible: boolean = false;
    tooltipTimeout: number | null = null;

    get pageEditUrl(): string {
        return '/cms/pages/edit?url=' + encodeURI(window.location.pathname);
    }

    get secondsLeftUntilNextSmsLabel(){
        return moment(this.secondsLeftUntilNextSms).format('m:ss')
    }

    get checkEmailText() {
        switch (this.model.checkEmailType) {
            case 'forgottenPassword':
                return i18next.t('label_password_recovery_submitted');
            case 'registration':
                return i18next.t('label_registration_submitted', <any>{
                    i: this.model.registrationEmail
                });
            default:
                return '';
        }
    }

    get registrationEmailError() {
        if (this.$v.model!.registrationEmail!.$error) {
            return i18next.t('error_email_wrong');
        }
        if (this.model.error) {
            return this.model.error;
        }

        return null;
    }

    get authEmailError() {
        if (this.$v.model!.email!.$error) {
            return i18next.t('error_email_wrong');
        }
        if (this.model.error) {
            return this.model.error;
        }

        return null;
    }

    get registrationPasswordError() {
        if (this.$v.model!.registrationPassword!.$error) {
            return i18next.t('error_required_field');
        }

        return null;
    }

    get userLabel(): string {
        if (this.$auth?.currentUser?.firstName || this.$auth?.currentUser?.lastName) {
            let result = this.$auth.currentUser.firstName;

            if (result) {
                result += '<br />';
            }

            result += this.$auth.currentUser.lastName;

            return result;
        } else {
            return this.$auth?.currentUser?.email
                ? this.$auth.currentUser.email
                : '';
        }
    }

    get welcomeEmailOrPhoneIsPhone() {
        return this.model.welcomeEmailOrPhone
            && !utils.String.isEmail(this.model.welcomeEmailOrPhone);
    }

    mounted() {
        // loadModel in different cases to prevent blinking
        if (this.$auth.authenticated) {
            this.$auth.getCurrentUser().then(
                (result: api.User) => {
                    this.loadModel();

                    this.allowCms = (result.roles! & Math.pow(2, 20)) == Math.pow(2, 20);
                    this.model.currentUserLoaded = true;
                },
                () => {
                    this.loadModel();

                    if (router.currentRoute.path === '/account') {
                        (<any>this.$refs).modalSignIn.isActive = true;
                    }
                }
            );
        } else {
            this.loadModel();
        }
    }

    loadModel() {
        this.model.state = this.model.state || 'welcome';
        this.model.submitted = false;
        this.model.error = '';
        this.model.transition = 'slide-right';
        this.model.currentUserLoaded = false;
    }

    onBack() {
        this.step('backward', this.model.previousState);
        this.model.previousState = '';
    }

    onClose() {
        (<any>this.$refs).modalSignIn.isActive = false;
        this.model.previousState = '';

        if (this.$router?.currentRoute?.path === '/account') {
            window.location.href = '/';
        }
    }

    onModalSignInShown() {
        if ((<any>this.$refs).welcomeEmailOrPhone) {
            (<any>this.$refs).welcomeEmailOrPhone.focus();
        }
    }

    onForgottenPassword() {
        (<any>this).$ga.sendEvent('password', 'btn.submit', 'password.btn.submit');

        if (!utils.String.isEmail(this.model.welcomeEmailOrPhone)) {
            this.model.previousState = 'password';
            this.step('forward', 'forgottenPassword');
        } else {
            this.$auth.forgottenPassword(this.model.welcomeEmailOrPhone).catch(
                (error) => {
                    if (error.response) {
                        console.log('XHR failed', error.response.status, error.response.data);
                    } else {
                        console.log('XHR error', error.message);
                    }
                }
            );

            this.model.checkEmailType = 'forgottenPassword';
            this.step('forward', 'checkEmail');
        }
    }

    onForgottenPasswordSubmit() {
        this.model.registrationEmailError = '';
        this.$v.model!.registrationEmail!.$touch();

        if (this.$v.model!.registrationEmail!.$error) {
            this.model.registrationEmailError = i18next.t('error_required_field');
        } else {
            this.model.submitted = true;

            (this.$auth).forgottenPassword(this.model.registrationEmail).then(
                () => {
                    this.model.checkEmailType = 'forgottenPassword';
                    this.model.submitted = false;
                    this.model.previousState = 'welcome';
                    this.step('forward', 'checkEmail');
                },
                (error) => {
                    this.model.submitted = false;

                    this.model.error = (error.response.data && error.response.data.localizedMessage)
                        ? error.response.data.localizedMessage
                        : i18next.t('error_server_tryagain');

                    if (error.response) {
                        console.log('XHR failed', error.response.status, error.response.data);
                    } else {
                        console.log('XHR error', error.message);
                    }
                }
            );
        }
    }

    onPasswordSubmit() {
        this.model.passwordError = '';
        this.$v.model!.password!.$touch();
        (<any>this).$ga.sendEvent('signin', 'btn.signin', 'signin.btn.signin');

        if (this.$v.model!.password!.$error) {
            this.model.passwordError = i18next.t('error_required_field');
        } else {
            this.model.submitted = true;

            (this.$auth).signIn(this.model.welcomeEmailOrPhone, this.model.password).then(
                () => {
                    this.model.submitted = false;

                    let redirect = '/account';

                    (<any>this).$ga.sendEvent('signin', 'success', 'signin.success');

                    if (this.$route.query.redirect) {
                        redirect = this.$route.query.redirect as string;
                    } else {
                        if (router.currentRoute
                            && router.currentRoute.path) {
                            redirect = router.currentRoute.path;
                        }
                    }

                    location.href = redirect;
                },
                (error) => {
                    this.model.submitted = false;

                    this.model.passwordError = (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain');

                    if (error.response) {
                        console.log('XHR failed', error.response.status, error.response.data);
                    } else {
                        console.log('XHR error', error.message);
                    }

                    (<any>this).$ga.sendEvent('signin', 'error', 'signin.error');
                }
            );
        }
    }

    onRegistration() {
        this.model.previousState = this.model.state;
        this.step('forward', 'registration');
        
        (<any>this).$ga.sendEvent('registration', 'btn.submit', 'registration.btn.submit');
    }

    setResendOneTimePasswordTimer(){
        this.secondsLeftUntilNextSms = 60000;
        this.isNextSmsAvaliable = false;
        const intervall = setInterval(() => {
            this.secondsLeftUntilNextSms -= 1000;
            
            if(this.secondsLeftUntilNextSms <= 0){
                clearInterval(intervall);
                this.isNextSmsAvaliable = true;
            }
        }, 1000);
    }

    onRegistrationSubmit() {
        this.model.error = '';

        this.$v.model!.registrationEmail!.$touch();
        this.$v.model!.registrationPassword!.$touch();

        if (!this.$v.model!.registrationEmail!.$error
            && !this.$v.model!.registrationPassword!.$error) {
            this.model.submitted = true;

            this.$auth.registration(
                this.model.registrationEmail,
                this.model.registrationPassword
            ).then(
                () => {
                    this.model.submitted = false;

                    this.model.checkEmailType = 'registration';
                    this.step('forward', 'checkEmail');

                    (<any>this).$ga.sendEvent('registration', 'success', 'registration.success');
                },
                (error) => {
                    this.model.submitted = false;

                    this.model.error = (error.response.data && error.response.data.localizedMessage)
                        ? error.response.data.localizedMessage
                        : i18next.t('error_server_tryagain');

                    if (error.response) {
                        console.log('XHR failed', error.response.status, error.response.data);
                    } else {
                        console.log('XHR error', error.message);
                    }

                    (<any>this).$ga.sendEvent('registration', 'error', 'registration.error');
                }
            );
        }
    }

    onWelcomeSubmit() {
        this.model.welcomeEmailOrPhoneError = '';
        this.$v.model!.welcomeEmailOrPhone!.$touch();

        if (this.$v.model!.welcomeEmailOrPhone!.$error) {
            this.model.welcomeEmailOrPhoneError = i18next.t('error_email_or_phone_empty');
        } else {
            this.model.submitted = true;

            (this.$auth).exists(this.model.welcomeEmailOrPhone).then(
                (result: boolean) => {
                    this.model.submitted = false;

                    if (!utils.String.isEmail(this.model.welcomeEmailOrPhone)) {
                        this.model.welcomeEmailOrPhone = this.$options.filters!.phone(this.model.welcomeEmailOrPhone);
                    }

                    this.model.previousState = 'welcome';
                    
                    if (result) {
                        this.step('forward', 'password');
                    } else {
                        this.step('forward', 'registration');
                    }
                },
                (error) => {
                    this.model.submitted = false;

                    this.model.welcomeEmailOrPhoneError = (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain');

                    if (error.response) {
                        console.log('XHR failed', error.response.status, error.response.data);
                    } else {
                        console.log('XHR error', error.message);
                    }
                }
            );
        }
    }

    onSmsWelcomeSubmit() {
        this.model.emailError = '';
        this.model.error = '';
        this.$v.model!.email!.$touch();

        if (this.$v.model!.email!.$error) {
            this.model.emailError = i18next.t('error_email_or_phone_empty');
        } else {
            this.model.submitted = true;
            (this.$auth).exists(this.model.email).then(
                (result: boolean) => {
                    this.model.submitted = false;
                    if(result){
                        this.sendOneTimePassword();
                    } else {
                        this.model.previousState = 'smsWelcome';
                        this.step('forward', 'registration');
                    }
                },
                (error) => {
                    this.model.submitted = false;
    
                    this.model.welcomeEmailOrPhoneError = (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain');
    
                    if (error.response) {
                        console.log('XHR failed', error.response.status, error.response.data);
                    } else {
                        console.log('XHR error', error.message);
                    }
                }
            );
            
        }
    }

    onOneTimePasswordSubmit() {
        this.model.error = '';
        this.model.oneTimePasswordError = '';
        this.$v.model!.oneTimePassword!.$touch();
        

        if (this.$v.model!.oneTimePassword!.$error) {
            this.model.oneTimePasswordError = i18next.t('error_required_field');
        } else {
            this.model.submitted = true;

            (this.$auth).signInByOneTimePassword(this.model.email, this.model.oneTimePassword).then(
                () => {
                    this.model.submitted = false;

                    let redirect = '/account';

                    if (this.$route.query.redirect) {
                        redirect = this.$route.query.redirect as string;
                    } else {
                        if (router.currentRoute
                            && router.currentRoute.path) {
                            redirect = router.currentRoute.path;
                        }
                    }

                    location.href = redirect;
                },
                (error) => {
                    this.model.submitted = false;

                    this.model.oneTimePasswordError = (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain');

                    if (error.response) {
                        console.log('XHR failed', error.response.status, error.response.data);
                    } else {
                        console.log('XHR error', error.message);
                    }

                    (<any>this).$ga.sendEvent('signin', 'error', 'signin.error');
                }
            );
        }
    }

    onSignIn() {
        // Cleanup any previous states
        this.model.state = 'welcome';
        this.loadModel();

        (<any>this.$refs).modalSignIn.isActive = true;

        this.$nextTick(() => {
            if ((<any>this.$refs).welcomeEmailOrPhone) {
                (<any>this.$refs).welcomeEmailOrPhone.focus();
            }
        });
    }

    onSignInByOneTimePassword() {
        // Cleanup any previous states
        this.model.state = 'smsWelcome';
        this.loadModel();
        (<any>this.$refs).modalSignIn.isActive = true;

        this.$nextTick(() => {
            if ((<any>this.$refs).smsWelcomeEmail) {
                (<any>this.$refs).smsWelcomeEmail.focus();
            }
        });
    }

    onTransitionAfterEnter() {
        switch (this.model.state) {
            case 'password':
                if ((<any>this.$refs).password) {
                    (<any>this.$refs).password.focus();
                }
                break;
            case 'registration':
                if ((<any>this.$refs).registrationEmail) {
                    (<any>this.$refs).registrationEmail.focus();
                } else {
                    (<any>this.$refs).registrationPassword.focus();
                }
                break;
            case 'welcome':
                if ((<any>this.$refs).welcomeEmailOrPhone) {
                    (<any>this.$refs).welcomeEmailOrPhone.focus();
                }
                break;
        }
    }

    sendOneTimePassword(){
        (this.$auth).sendOneTimePassword(this.model.email).then((result:boolean)=>{
            this.model.submitted = false;
            this.model.previousState = 'smsWelcome';
            this.step('forward', 'oneTimePassword');
            this.setResendOneTimePasswordTimer();
        },
        (error)=>{
            this.model.submitted = false;

            this.model.error = (error.response && error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain');

            if (error.response) {
                console.log('XHR failed', error.response.status, error.response.data);
            } else {
                console.log('XHR error', error.message);
            }
        })
    }
    
    step(direction: 'backward' | 'forward', targetState: PanelSigninComponentState | '') {
        if (targetState === '') {
            return;
        }

        switch (targetState) {
            case 'registration':
                this.model.registrationEmail = '';
                if (this.model.welcomeEmailOrPhone && utils.String.isEmail(this.model.welcomeEmailOrPhone)) {
                    this.model.registrationEmail = this.model.welcomeEmailOrPhone;
                }
                if (this.model.email) {
                    this.model.registrationEmail = this.model.email;
                }
                this.model.registrationPassword = '';
                break;
        }

        if (direction === 'forward') {
            this.model.transition = 'slide-right';
        } else {
            this.model.transition = 'slide-left';
        }
        this.model.state = targetState;
    }

    onWelcomeEmailOrPhoneInput(){
        this.model.welcomeEmailOrPhone = utils.String.disabledCyrillic(
            this.model.welcomeEmailOrPhone,
            () => {
                this.showTooltip(
                    (<any>this.$refs).welcomeEmailOrPhone.$el,
                    i18next.t('error_letters_latin_numbers_symbols_only_no_specifics')
                );
            },
        );

        (<any>this.$refs).welcomeEmailOrPhone.lazyValue = this.model.welcomeEmailOrPhone;
    }

    showTooltip(target, text) {
        (<any>this).$refs.tooltip.isActive = false;

        if (this.tooltipTimeout) {
            clearTimeout(this.tooltipTimeout);
        }

        this.$nextTick(() => {
            this.tooltipText = text;
            this.tooltipActivator = target;
            (<any>this).$refs.tooltip.isActive = true;

            this.tooltipTimeout = window.setTimeout(() => {
                (<any>this).$refs.tooltip.isActive = false;
            }, 2000);
        });
    }
}
